// Class step component - Step 0 of character creation wizard

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { getChoiceLabel, getSelectionType, isExpertiseChoice, renderMarkdown, slugToTitle } from '../../lib/format.js';
import { renderEquipmentPackages } from '../../lib/render-helpers.js';
import './option-card.js';
import './skill-option-card.js';
import './spell-option-card.js';
import './weapon-option-card.js';

/**
 * @typedef {import('../views/create-character-view.js').ClassListItem} ClassListItem
 * @typedef {import('../views/create-character-view.js').ClassCreationOptions} ClassCreationOptions
 * @typedef {import('../views/create-character-view.js').EquipmentPackage} EquipmentPackage
 * @typedef {import('../views/create-character-view.js').ClassFeature} ClassFeature
 * @typedef {import('../views/create-character-view.js').SkillOption} SkillOption
 * @typedef {import('../../types/jsdoc-types.js').ChoiceOption} ChoiceOption
 * @typedef {import('../../types/jsdoc-types.js').FeatureChoice} FeatureChoice
 */

/**
 * Class selection step component.
 * Controlled component: receives all data as properties, emits events for mutations.
 *
 * Events: select-class, toggle-skill, select-choice-option,
 * toggle-choice-option, select-equipment-package
 */
class ClassStep extends BaseComponent {
  static properties = {
    classes: { type: Array },
    selectedSlug: { type: String, attribute: 'selected-slug' },
    classOptions: { type: Object },
    skillProficiencies: { type: Array },
    featureChoices: { type: Object },
    equipmentPackageId: { type: String },
    isHeaderStuck: { type: Boolean, attribute: 'is-header-stuck' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .class-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 1rem;
    }

    .selectable-card {
      cursor: pointer;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
    }

    .selectable-card:hover:not(.disabled) {
      border-color: #0d6efd;
    }

    .selectable-card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 1px #0d6efd;
      background-color: rgba(13, 110, 253, 0.04);
    }

    .sticky-header {
      position: sticky;
      top: 0;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(8px);
      z-index: 10;
      padding-top: 0.5rem;
      padding-bottom: 1rem;
      margin-top: -0.5rem;
      margin-left: -1rem;
      margin-right: -1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease;
    }

    .sticky-header.stuck {
      border-bottom-color: rgba(0, 0, 0, 0.125);
      padding-top: 1rem;
    }
  `;

  constructor() {
    super();
    /** @type {Array<ClassListItem>} */
    this.classes = [];
    this.selectedSlug = '';
    /** @type {ClassCreationOptions | null} */
    this.classOptions = null;
    /** @type {Array<string>} */
    this.skillProficiencies = [];
    /** @type {{[key: string]: Array<string>}} */
    this.featureChoices = {};
    this.equipmentPackageId = '';
    this.isHeaderStuck = false;
  }

  /**
   * @param {FeatureChoice} choice
   * @returns {number}
   */
  _getChoiceRequiredCount(choice) {
    if (getSelectionType(choice) === 'single') return 1;
    if (choice.countModifiedBy) {
      const { feature: modFeature, option: modOption, count: modCount } = choice.countModifiedBy;
      for (let i = 0; i < 10; i++) {
        const featureSelections = this.featureChoices[`${modFeature}:${i}`] || [];
        if (featureSelections.includes(modOption)) return modCount;
      }
    }
    return choice.count || 1;
  }

  render() {
    return html`
      <div>
        <div class="sticky-header ${this.isHeaderStuck ? 'stuck' : ''}">
          <h5 class="card-title mb-0">Choose Your Class</h5>
        </div>
        <p class="text-muted mb-3">Your class determines your core class traits and class features.</p>

        <div class="class-grid mb-4">
          ${this.classes.map(
            (cls) => html`
              <div
                class="card h-100 selectable-card ${this.selectedSlug === cls.slug ? 'selected' : ''}"
                @click="${() => this._onSelectClass(cls.slug)}"
              >
                <div class="card-body">
                  <h6 class="card-title mb-1">${cls.name}</h6>
                  <p class="card-text small mb-2">${cls.description}</p>
                  <div class="small mb-1 fst-italic">Primary Ability: ${cls.primaryAbility}</div>
                  <div class="small mb-1 fst-italic">Likes: ${cls.likes}</div>
                  <div class="small fst-italic text-muted">
                    Complexity: <span class="text-capitalize">${cls.complexity}</span>
                  </div>
                </div>
              </div>
            `,
          )}
        </div>

        ${this.classOptions ? this._renderClassOptions() : ''}
      </div>
    `;
  }

  _renderClassOptions() {
    const options = this.classOptions;
    if (!options) return html``;
    return html`
      <!-- Core Traits -->
      <h5 class="mt-4 mb-3">Core Traits</h5>

      <div class="mb-3">
        <h6 class="mb-0 d-inline">Primary Ability:</h6>
        ${options.coreTraits.primaryAbility}
      </div>

      <div class="mb-3">
        <h6 class="mb-0 d-inline">Hit Point Die:</h6>
        D${options.coreTraits.hitDie} per ${options.class.name} level
      </div>

      <div class="mb-3">
        <h6 class="mb-0 d-inline">Saving Throw Proficiencies:</h6>
        ${options.coreTraits.savingThrowProficiencies.join(' and ')}
      </div>

      <!-- Skill Proficiencies -->
      ${
        options.skillProficiencies
          ? html`
            <div class="mb-3">
              <div class="mb-2">
                <h6 class="mb-0 d-inline">Skill Proficiencies</h6>
                <span class="badge bg-primary ms-1"
                  >${this.skillProficiencies.length} / ${options.skillProficiencies.count}</span
                >
              </div>
              <div class="row g-2">
                ${options.skillProficiencies.options.map((/** @type {SkillOption} */ skill) => {
                  const slug = skill.slug;
                  const name = skill.name || slug.replace(/-/g, ' ');
                  const ability = skill.ability || '';
                  const isSelected = this.skillProficiencies.includes(slug);
                  const isDisabled =
                    !isSelected && this.skillProficiencies.length >= (options.skillProficiencies?.count ?? 0);
                  const recommendedSkills = options.skillProficiencies?.recommended || [];
                  return html`
                    <div class="col-12 col-md-6 col-lg-3">
                      <skill-option-card
                        name="${name}"
                        slug="${slug}"
                        ability="${ability}"
                        ?selected="${isSelected}"
                        ?disabled="${isDisabled}"
                        ?recommended="${recommendedSkills.includes(slug)}"
                        @card-selected="${() => !isDisabled && this._onToggleSkill(slug)}"
                      ></skill-option-card>
                    </div>
                  `;
                })}
              </div>
            </div>
          `
          : ''
      }

      <div class="mb-3">
        <h6 class="mb-0 d-inline">Weapon Proficiencies:</h6>
        ${options.coreTraits.weaponProficiencies?.join(', ') || 'None'}
      </div>

      <div class="mb-3">
        <h6 class="mb-0 d-inline">Armor Training:</h6>
        ${options.coreTraits.armorTraining?.join(', ') || 'None'}
      </div>

      <!-- Equipment Packages -->
      ${
        (options.equipmentPackages?.length ?? 0) > 0
          ? renderEquipmentPackages(
              /** @type {Array<EquipmentPackage>} */ (options.equipmentPackages),
              this.equipmentPackageId,
              'equipment-package',
              (slug) => this._onSelectEquipmentPackage(slug),
            )
          : ''
      }

      <!-- Class Features -->
      ${
        (options.features?.length ?? 0) > 0
          ? html`
            <h5 class="mt-4 mb-3">Class Features</h5>
            ${(options.features ?? []).map(
              (/** @type {ClassFeature} */ feature) => html`
                <div class="mb-4">
                  <h6 class="mb-2">${feature.name}</h6>
                  <div class="text-muted small mb-3">${renderMarkdown(feature.description)}</div>

                  ${feature.choices?.map((choice, choiceIndex) =>
                    this._renderFeatureChoice(feature.slug, choiceIndex, choice),
                  )}
                </div>
              `,
            )}
          `
          : ''
      }
    `;
  }

  /**
   * Evaluates a choice's `visibleWhen` predicate against the player's
   * current featureChoices. Returns true when the choice has no
   * predicate, or when the named feature's selection includes the
   * named option. Mirrors the lookup pattern in
   * _getChoiceRequiredCount.
   *
   * @param {FeatureChoice} choice
   * @returns {boolean}
   */
  _isChoiceVisible(choice) {
    if (!choice.visibleWhen) return true;
    const { feature: predFeature, option: predOption } = choice.visibleWhen;
    for (let i = 0; i < 10; i++) {
      const selections = this.featureChoices[`${predFeature}:${i}`] || [];
      if (selections.includes(predOption)) return true;
    }
    return false;
  }

  /**
   * Slugs already picked in sibling spell choices that share the same
   * list/level filter (e.g. cleric cantrips picked in Spellcasting
   * shouldn't be re-pickable as the Thaumaturge extra cantrip in
   * Divine Order, and vice versa). The current choice's own
   * selections are excluded so the player can deselect their own
   * picks normally.
   *
   * @param {string} featureSlug
   * @param {number} choiceIndex
   * @param {FeatureChoice} choice
   * @returns {Set<string>}
   */
  _siblingSpellSelections(featureSlug, choiceIndex, choice) {
    const taken = new Set();
    if (choice.choiceType !== 'spell') return taken;
    const myFilter = choice.filter || {};
    for (const feature of this.classOptions?.features ?? []) {
      const choices = feature.choices ?? [];
      for (let i = 0; i < choices.length; i++) {
        if (feature.slug === featureSlug && i === choiceIndex) continue;
        const sibling = /** @type {FeatureChoice} */ (choices[i]);
        if (sibling?.choiceType !== 'spell') continue;
        const sibFilter = sibling.filter || {};
        if (sibFilter.list !== myFilter.list) continue;
        if (sibFilter.level !== myFilter.level) continue;
        const sels = this.featureChoices[`${feature.slug}:${i}`] || [];
        for (const s of sels) taken.add(s);
      }
    }
    return taken;
  }

  /**
   * @param {string} featureSlug
   * @param {number} choiceIndex
   * @param {FeatureChoice} choice
   */
  _renderFeatureChoice(featureSlug, choiceIndex, choice) {
    if (!this._isChoiceVisible(choice)) return html``;
    const key = `${featureSlug}:${choiceIndex}`;
    const selections = this.featureChoices[key] || [];
    const requiredCount = this._getChoiceRequiredCount(choice);
    const takenInSiblings = this._siblingSpellSelections(featureSlug, choiceIndex, choice);

    // Resolve placeholder options of the form "{choices.<id>}" by reading
    // the sibling choice on the same feature: pull its already-resolved
    // option objects, intersect with the player's current selections, and
    // use the resulting subset as this choice's options. If the source
    // hasn't been picked yet, render a hint and disable the picker.
    let placeholderHint = '';
    if (typeof choice.options === 'string') {
      const match = choice.options.match(/^\{choices\.([^}]+)\}$/);
      if (match) {
        const sourceId = match[1];
        const feature = this.classOptions?.features?.find((/** @type {ClassFeature} */ f) => f.slug === featureSlug);
        const sourceIndex = feature?.choices?.findIndex((/** @type {FeatureChoice} */ c) => c.id === sourceId) ?? -1;
        const sourceChoice = sourceIndex >= 0 ? feature?.choices?.[sourceIndex] : undefined;
        const sourceSelections = sourceIndex >= 0 ? this.featureChoices[`${featureSlug}:${sourceIndex}`] || [] : [];
        const sourceOptions = Array.isArray(sourceChoice?.options) ? sourceChoice.options : [];
        if (sourceSelections.length === 0) {
          const sourceLabel = sourceChoice ? getChoiceLabel(/** @type {FeatureChoice} */ (sourceChoice)) : sourceId;
          placeholderHint = `Choose your ${sourceLabel} spells first.`;
          choice = { ...choice, options: [] };
        } else {
          const filtered = sourceOptions.filter((/** @type {ChoiceOption} */ o) => sourceSelections.includes(o.slug));
          choice = { ...choice, options: filtered };
        }
      }
    }

    let recommendedSlugs;
    if (choice.choiceType === 'weapon') {
      const pkg = this.classOptions?.equipmentPackages?.find((p) => p.slug === this.equipmentPackageId);
      const equipSlugs = new Set((pkg?.items || []).map((i) => i.slug));
      const opts = Array.isArray(choice.options) ? choice.options : [];
      const weaponSlugs = opts.map((/** @type {ChoiceOption | string} */ o) => (typeof o === 'string' ? o : o.slug));
      recommendedSlugs = new Set(weaponSlugs.filter((/** @type {string} */ s) => equipSlugs.has(s)));
    } else {
      recommendedSlugs = new Set(
        Array.isArray(choice.recommended) ? choice.recommended : choice.recommended ? [choice.recommended] : [],
      );
    }

    // Expertise type: show selected skill proficiencies as options
    if (isExpertiseChoice(choice)) {
      return html`
        <div class="mb-3">
          <div class="mb-2">
            <span class="fst-italic">${getChoiceLabel(choice)}</span>
            <span class="badge bg-primary ms-2">${selections.length} / ${requiredCount}</span>
          </div>
          <div class="text-muted small mb-2">${renderMarkdown(choice.description)}</div>
          <div class="row g-2">
            ${this.skillProficiencies.map((skillSlug) => {
              const isSelected = selections.includes(skillSlug);
              const isDisabled = !isSelected && selections.length >= requiredCount;
              return html`
                <div class="col-12 col-md-6 col-lg-3">
                  <option-card
                    name="${slugToTitle(skillSlug)}"
                    slug="${skillSlug}"
                    ?selected="${isSelected}"
                    ?disabled="${isDisabled}"
                    input-type="checkbox"
                    @card-selected="${() =>
                      !isDisabled && this._onToggleChoiceOption(featureSlug, choiceIndex, skillSlug, requiredCount)}"
                  ></option-card>
                </div>
              `;
            })}
          </div>
        </div>
      `;
    }

    return html`
      <div class="mb-3">
        <div class="mb-2">
          <span class="fst-italic">${getChoiceLabel(choice)}</span>
          <span class="badge bg-primary ms-2">${selections.length} / ${requiredCount}</span>
        </div>
        <div class="text-muted small mb-2">${renderMarkdown(choice.description)}</div>
        ${placeholderHint ? html`<div class="text-muted fst-italic small mb-2">${placeholderHint}</div>` : ''}

        ${
          getSelectionType(choice) === 'single'
            ? html`
              <div class="row g-2">
                ${
                  /** @type {Array<ChoiceOption>|undefined} */ (choice.options)?.map(
                    (option) => html`
                      <div class="col-12 col-md-6 col-lg-3">
                        ${
                          option.school
                            ? html`
                              <spell-option-card
                                name="${option.name}"
                                slug="${option.slug}"
                                school="${option.school}"
                                ?concentration="${option.concentration}"
                                description="${option.description || ''}"
                                ?selected="${selections.includes(option.slug)}"
                                ?recommended="${option.recommended || recommendedSlugs.has(option.slug)}"
                                input-type="radio"
                                radio-group="${key}"
                                @card-selected="${(/** @type {CustomEvent} */ e) =>
                                  this._onSelectChoiceOption(featureSlug, choiceIndex, e.detail.slug)}"
                              ></spell-option-card>
                            `
                            : option.masteryProperty
                              ? html`
                                <weapon-option-card
                                  name="${option.name}"
                                  slug="${option.slug}"
                                  mastery-property="${option.masteryProperty}"
                                  ?selected="${selections.includes(option.slug)}"
                                  ?recommended="${option.recommended || recommendedSlugs.has(option.slug)}"
                                  input-type="radio"
                                  radio-group="${key}"
                                  @card-selected="${(/** @type {CustomEvent} */ e) =>
                                    this._onSelectChoiceOption(featureSlug, choiceIndex, e.detail.slug)}"
                                ></weapon-option-card>
                              `
                              : html`
                                <option-card
                                  name="${option.name}"
                                  slug="${option.slug}"
                                  description="${option.description || ''}"
                                  ?selected="${selections.includes(option.slug)}"
                                  ?recommended="${option.recommended || recommendedSlugs.has(option.slug)}"
                                  input-type="radio"
                                  radio-group="${key}"
                                  @card-selected="${(/** @type {CustomEvent} */ e) =>
                                    this._onSelectChoiceOption(featureSlug, choiceIndex, e.detail.slug)}"
                                ></option-card>
                              `
                        }
                      </div>
                    `,
                  )
                }
              </div>
            `
            : getSelectionType(choice) === 'multi'
              ? html`
                <div class="row g-2">
                  ${
                    /** @type {Array<ChoiceOption>|undefined} */ (choice.options)?.map((option) => {
                      const isSelected = selections.includes(option.slug);
                      const isKnown = !isSelected && takenInSiblings.has(option.slug);
                      const isDisabled = isKnown || (!isSelected && selections.length >= requiredCount);
                      return html`
                        <div class="col-12 col-md-6 col-lg-3">
                          ${
                            option.school
                              ? html`
                                <spell-option-card
                                  name="${option.name}"
                                  slug="${option.slug}"
                                  school="${option.school}"
                                  ?concentration="${option.concentration}"
                                  description="${option.description || ''}"
                                  ?selected="${isSelected || isKnown}"
                                  ?disabled="${isDisabled}"
                                  ?known="${isKnown}"
                                  ?recommended="${option.recommended || recommendedSlugs.has(option.slug)}"
                                  input-type="checkbox"
                                  @card-selected="${(/** @type {CustomEvent} */ e) =>
                                    !isDisabled &&
                                    this._onToggleChoiceOption(featureSlug, choiceIndex, e.detail.slug, requiredCount)}"
                                ></spell-option-card>
                              `
                              : option.masteryProperty
                                ? html`
                                  <weapon-option-card
                                    name="${option.name}"
                                    slug="${option.slug}"
                                    mastery-property="${option.masteryProperty}"
                                    ?selected="${isSelected}"
                                    ?disabled="${isDisabled}"
                                    ?recommended="${option.recommended || recommendedSlugs.has(option.slug)}"
                                    input-type="checkbox"
                                    @card-selected="${(/** @type {CustomEvent} */ e) =>
                                      !isDisabled &&
                                      this._onToggleChoiceOption(
                                        featureSlug,
                                        choiceIndex,
                                        e.detail.slug,
                                        requiredCount,
                                      )}"
                                  ></weapon-option-card>
                                `
                                : html`
                                  <option-card
                                    name="${option.name}"
                                    slug="${option.slug}"
                                    description="${option.description || ''}"
                                    ?selected="${isSelected || isKnown}"
                                    ?disabled="${isDisabled}"
                                    ?known="${isKnown}"
                                    ?recommended="${option.recommended || recommendedSlugs.has(option.slug)}"
                                    input-type="checkbox"
                                    @card-selected="${(/** @type {CustomEvent} */ e) =>
                                      !isDisabled &&
                                      this._onToggleChoiceOption(
                                        featureSlug,
                                        choiceIndex,
                                        e.detail.slug,
                                        requiredCount,
                                      )}"
                                  ></option-card>
                                `
                          }
                        </div>
                      `;
                    })
                  }
                </div>
              `
              : ''
        }
      </div>
    `;
  }

  /** @param {string} slug */
  _onSelectClass(slug) {
    this.dispatchEvent(new CustomEvent('select-class', { detail: { slug }, bubbles: true, composed: true }));
  }

  /** @param {string} slug */
  _onToggleSkill(slug) {
    this.dispatchEvent(new CustomEvent('toggle-skill', { detail: { slug }, bubbles: true, composed: true }));
  }

  /**
   * @param {string} featureSlug
   * @param {number} choiceIndex
   * @param {string} optionSlug
   */
  _onSelectChoiceOption(featureSlug, choiceIndex, optionSlug) {
    this.dispatchEvent(
      new CustomEvent('select-choice-option', {
        detail: { featureSlug, choiceIndex, optionSlug },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * @param {string} featureSlug
   * @param {number} choiceIndex
   * @param {string} optionSlug
   * @param {number} maxCount
   */
  _onToggleChoiceOption(featureSlug, choiceIndex, optionSlug, maxCount) {
    this.dispatchEvent(
      new CustomEvent('toggle-choice-option', {
        detail: { featureSlug, choiceIndex, optionSlug, maxCount },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** @param {string} slug */
  _onSelectEquipmentPackage(slug) {
    this.dispatchEvent(
      new CustomEvent('select-equipment-package', { detail: { slug }, bubbles: true, composed: true }),
    );
  }
}

customElements.define('class-step', ClassStep);
